import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useConnection } from '../connection/Application';
import Header from '../components/Header/header';
import QuestionNavigator from '../components/Cba/QuestionNavigator';
import ExamQuestionView from '../components/Cba/ExamQuestion';
import SingleSheetPaper from '../components/Cba/SingleSheetPaper';
import useExamClock from '../components/Cba/useExamClock';
import useExamWarnings from '../components/Cba/useExamWarnings';
import useAutosave from '../components/Cba/useAutosave';
import { statusLabel } from '../components/Cba/autosaveQueue';
import {
  AnswerMap,
  clampIndex,
  countStates,
  ExamAttempt,
  ExamPaper,
  ExamQuestion,
  firstUnanswered,
  isLast,
  nextIndex,
  previousIndex,
  questionStates,
  readSeen,
  submitMessage,
  unansweredCount,
  toAnswerMap,
  writeSeen,
} from '../components/Cba/examState';
import { CbaPaperDetail, formatRemaining, paperCrumbs, readBody } from '../components/Cba/cbaApi';

/**
 * Sitting a CBA mock paper: one question at a time, with a navigator showing
 * what has been answered, what was skipped and what has not been opened.
 *
 * Starting and resuming are the same call, so a refresh, a dropped connection
 * or a second device all land back in the same sitting with the time that
 * remains.
 */
/** Height of the site's fixed app bar, which changes with the window width. */
const useFixedHeaderHeight = (): number => {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const measure = () =>
      setHeight(document.querySelector('.MuiAppBar-root')?.getBoundingClientRect().height ?? 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return height;
};

const CbaExam: FC = () => {
  const { paperId } = useParams();
  const connection = useConnection();
  // The paper is opened once per paperId. `connection` is rebuilt whenever the
  // sign-in token changes (a refresh, a sign-in in another tab), and opening the
  // paper again then would put a student back on the loading screen mid-sitting.
  const connectionRef = useRef(connection);
  connectionRef.current = connection;
  const navigate = useNavigate();

  const [attempt, setAttempt] = useState<ExamAttempt>();
  const [paper, setPaper] = useState<ExamPaper>();
  // Only for the breadcrumb: the attempt payload carries no subject or grade.
  const [paperDetail, setPaperDetail] = useState<CbaPaperDetail>();
  const [questions, setQuestions] = useState<ExamQuestion[]>([]);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [seen, setSeen] = useState<string[]>([]);
  const [current, setCurrent] = useState(0);
  const [serverNow, setServerNow] = useState<string>();
  const attemptRef = useRef<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);

  // Counts down against the deadline the server issued, not this device's clock.
  const { remainingSeconds, expired, resync } = useExamClock(attempt?.deadlineAt, serverNow);

  // The marks come from the paper: a short paper carries its own.
  const { warning, dismiss } = useExamWarnings(remainingSeconds, paper?.warnAtMinutes);

  const startOrResume = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await connectionRef.current.post(`cba/papers/${paperId}/attempts`, {});
      const payload = readBody<any>(response);

      const loadedQuestions: ExamQuestion[] = payload?.questions ?? [];
      const loadedAnswers = toAnswerMap(payload?.answers);

      attemptRef.current = payload?.attempt?.id ?? '';
      setAttempt(payload?.attempt);
      setPaper(payload?.paper);
      setServerNow(payload?.serverNow);
      setQuestions(loadedQuestions);
      setAnswers(loadedAnswers);

      // A resumed paper opens where the student left off rather than back at
      // question one.
      const opensAt = payload?.attempt?.resumed
        ? firstUnanswered(loadedQuestions, loadedAnswers)
        : 0;
      setCurrent(opensAt);

      const previouslySeen = readSeen(payload?.attempt?.id ?? '');
      const openingId = loadedQuestions[opensAt]?.questionId;
      setSeen(
        openingId && !previouslySeen.includes(openingId)
          ? [...previouslySeen, openingId]
          : previouslySeen,
      );
    } catch (e: any) {
      const message = typeof e?.message === 'string' ? e.message : '';
      setError(message !== '' ? message : 'This paper could not be opened. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [paperId]);

  useEffect(() => {
    startOrResume();
  }, [startOrResume]);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const response = await connectionRef.current.get(`cba/papers/${paperId}`);
        setPaperDetail(readBody<CbaPaperDetail>(response));
      } catch (e) {
        // Without it the breadcrumb is just shorter.
        setPaperDetail(undefined);
      }
    };
    fetchDetail();
  }, [paperId]);

  // Opening a question is what separates a skipped one from one never seen, and
  // nothing on the server records it, so it is kept against the attempt here.
  const markSeen = useCallback(
    (index: number) => {
      const questionId = questions[index]?.questionId;
      if (!questionId || !attempt) {
        return;
      }
      setSeen((previous) => {
        if (previous.includes(questionId)) {
          return previous;
        }
        const updated = [...previous, questionId];
        writeSeen(attempt.id, updated);
        return updated;
      });
    },
    [attempt, questions],
  );

  const sendAnswers = useCallback(
    (batch: any) => connection.patch(`cba/attempts/${attemptRef.current}/answers`, batch),
    [connection],
  );

  const { queueAnswer, flush, status, pendingCount } = useAutosave(
    attempt?.id,
    sendAnswers,
    // Every save response carries the server's time, so the clock corrects
    // itself as the paper goes on rather than drifting from one reading.
    (response) => resync(readBody<any>(response)?.serverNow ?? response?.serverNow),
  );

  const setAnswer = useCallback(
    (questionId: string, value: string[]) => {
      // On screen at once, sent separately. A slow connection never blocks the
      // paper.
      setAnswers((previous) => ({ ...previous, [questionId]: value }));
      queueAnswer(questionId, value);
    },
    [queueAnswer],
  );

  const goTo = useCallback(
    (index: number) => {
      const target = clampIndex(index, questions.length);
      setCurrent(target);
      markSeen(target);
    },
    [markSeen, questions.length],
  );

  const submit = useCallback(
    async (source: 'manual' | 'auto') => {
      if (submittedRef.current || !attemptRef.current) {
        return;
      }
      submittedRef.current = true;
      setSubmitting(true);
      setConfirming(false);

      // Send anything still queued first, so the last answer a student chose is
      // never the one left behind.
      try {
        await flush();
      } catch (e) {
        // A failed save must not stop the paper being submitted: the answers
        // that did reach the server are still marked.
      }

      try {
        await connection.post(`cba/attempts/${attemptRef.current}/submit`, { source });
        navigate(`/cba/attempts/${attemptRef.current}/result`, { replace: true });
      } catch (e: any) {
        // Submitting twice returns the same result, so an error here is a real
        // failure rather than a duplicate.
        submittedRef.current = false;
        setSubmitting(false);
        const message = typeof e?.message === 'string' ? e.message : '';
        setError(message !== '' ? message : 'The paper could not be submitted. Please try again.');
      }
    },
    [connection, flush, navigate],
  );

  // The clock running out submits the paper. This is a convenience, not the
  // guarantee: a closed tab never reaches it, and the server closes and marks
  // any paper found past its deadline.
  useEffect(() => {
    if (expired && attempt && !submittedRef.current) {
      submit('auto');
    }
  }, [expired, attempt, submit]);

  const singleSheet = paper?.mode === 'single';

  const states = useMemo(
    () =>
      questionStates(
        questions,
        answers,
        singleSheet ? questions.map((entry) => entry.questionId) : seen,
      ),
    [questions, answers, seen, singleSheet],
  );

  const question = questions[current];
  const headerHeight = useFixedHeaderHeight();

  const crumbs = paperCrumbs(paperDetail, paper?.title ?? '', `/cba/papers/${paperId}/sit`);

  if (loading) {
    return (
      <Container sx={{ minHeight: 'calc(100vh - 185px)' }}>
        <CssBaseline />
        <Header category={false} x={{ crumbs }} />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error !== '' || !attempt || !paper) {
    return (
      <Container sx={{ minHeight: 'calc(100vh - 185px)' }}>
        <CssBaseline />
        <Header category={false} x={{ crumbs }} />
        <Box sx={{ pt: 10 }}>
          <Alert severity='error'>{error || 'This paper could not be opened.'}</Alert>
          <Button sx={{ mt: 2 }} onClick={() => navigate(`/cba/papers/${paperId}`)}>
            Back to the paper
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth='lg' sx={{ minHeight: 'calc(100vh - 185px)', pb: 6 }}>
      <CssBaseline />
      <Header category={false} x={{ crumbs }} />

      {/* The site header is fixed, so the clock sticks just below it. */}
      <Box
        sx={{
          position: 'sticky',
          top: headerHeight,
          // The header's own spacer is shorter than the bar with the subject tabs.
          mt: 12,
          zIndex: 2,
          background: '#fff',
          borderBottom: '1px solid #E1E7E5',
          py: 2,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant='h6' sx={{ fontWeight: 600 }} noWrap>
            {paper.title}
          </Typography>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            Attempt {attempt.attemptNo} · {questions.length} questions · {attempt.totalMarks} marks
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'right' }}>
          <Typography
            variant='h5'
            sx={{
              fontVariantNumeric: 'tabular-nums',
              fontWeight: 700,
              // The last five minutes are worth noticing without a dialog.
              color: expired ? '#A32A1E' : remainingSeconds <= 300 ? '#A32A1E' : 'inherit',
            }}
          >
            {formatRemaining(remainingSeconds)}
          </Typography>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            {expired ? 'Time is up' : 'remaining'}
          </Typography>
        </Box>
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mt: 3 }}>
        {singleSheet ? (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {questions.length === 0 ? (
              <Alert severity='warning'>This paper has no questions to show.</Alert>
            ) : (
              <>
                <SingleSheetPaper
                  questions={questions}
                  answers={answers}
                  disabled={expired || submitting}
                  onChange={setAnswer}
                />

                <Card sx={{ p: 3, mt: 2, borderRadius: 3, boxShadow: '0 2px 12px #0000001A' }}>
                  <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
                    {unansweredCount(questions, answers) === 0
                      ? 'Every question has an answer.'
                      : `${unansweredCount(questions, answers)} question${
                          unansweredCount(questions, answers) === 1 ? '' : 's'
                        } still without an answer.`}
                  </Typography>
                  <Button
                    variant='contained'
                    color='success'
                    size='large'
                    disabled={submitting || expired}
                    onClick={() => setConfirming(true)}
                  >
                    {submitting ? 'Submitting' : 'Submit paper'}
                  </Button>
                </Card>
              </>
            )}
          </Box>
        ) : (
          <Card
            sx={{ p: 3, borderRadius: 3, flex: 1, minWidth: 0, boxShadow: '0 2px 12px #0000001A' }}
          >
            {question ? (
              <>
                <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>
                  Question {current + 1} of {questions.length} · {question.marks} mark
                  {question.marks === 1 ? '' : 's'}
                </Typography>

                <Box sx={{ mt: 2, mb: 3 }}>
                  <ExamQuestionView
                    question={question}
                    value={answers[question.questionId] ?? []}
                    disabled={expired || submitting}
                    onChange={(value) => setAnswer(question.questionId, value)}
                  />
                </Box>

                <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  <Button
                    variant='outlined'
                    disabled={current === 0}
                    onClick={() => goTo(previousIndex(current, questions.length))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant='outlined'
                    disabled={isLast(current, questions.length)}
                    onClick={() => goTo(nextIndex(current, questions.length))}
                  >
                    Skip
                  </Button>
                  <Box sx={{ flex: 1 }} />
                  <Button
                    variant='outlined'
                    disabled={isLast(current, questions.length)}
                    onClick={() => goTo(nextIndex(current, questions.length))}
                  >
                    Next
                  </Button>
                  <Button
                    variant='contained'
                    color='success'
                    disabled={submitting || expired}
                    onClick={() => setConfirming(true)}
                  >
                    {submitting ? 'Submitting' : 'Submit paper'}
                  </Button>
                </Stack>
              </>
            ) : (
              <Alert severity='warning'>This paper has no questions to show.</Alert>
            )}
          </Card>
        )}

        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            width: { xs: '100%', md: '20rem' },
            flexShrink: 0,
            alignSelf: 'flex-start',
            boxShadow: '0 2px 12px #0000001A',
          }}
        >
          <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 0.5 }}>
            Your paper
          </Typography>
          <Typography
            variant='caption'
            sx={{
              display: 'block',
              mb: 2,
              color: status === 'error' ? '#A32A1E' : 'text.secondary',
            }}
          >
            {statusLabel(status, pendingCount)}
          </Typography>
          {singleSheet ? (
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              {countStates(states).answered} of {questions.length} answered. Every question is on
              this page, so scroll to move between them.
            </Typography>
          ) : (
            <QuestionNavigator states={states} current={current} onJump={goTo} />
          )}
        </Card>
      </Stack>

      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>Submit this paper?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {submitMessage(unansweredCount(questions, answers))}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Keep working</Button>
          <Button variant='contained' color='success' onClick={() => submit('manual')}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={warning !== undefined}
        autoHideDuration={8000}
        onClose={dismiss}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={dismiss}
          severity={warning !== undefined && warning <= 5 ? 'error' : 'warning'}
          variant='filled'
          role='alert'
          sx={{ width: '100%' }}
        >
          {warning} minute{warning === 1 ? '' : 's'} remaining
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CbaExam;
